import React from 'react';
import { Facebook, Instagram, Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import { Logo } from './Logo';

export const Footer = ({ t, onNavigate }) => {
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    { key: 'home', section: 'hero' },
    { key: 'about', section: 'about' },
    { key: 'properties', section: 'properties' },
    { key: 'services', section: 'services' },
    { key: 'contact', section: 'contact' }
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook', color: 'hover:bg-[#1877F2]' },
    { icon: Instagram, href: '#', label: 'Instagram', color: 'hover:bg-gradient-to-tr hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF]' },
    { icon: Linkedin, href: '#', label: 'LinkedIn', color: 'hover:bg-[#0A66C2]' }
  ];

  return (
    <footer className="relative bg-black border-t border-white/10 overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#C5A05E]/10 rounded-full blur-3xl" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C5A05E]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="md:col-span-2 space-y-6">
            <Logo size="lg" showText={true} />
            <p className="text-gray-400 leading-relaxed max-w-md">
              {t.footer.description}
            </p>
            <div className="space-y-3">
              {[
                { icon: Mail, text: 'info@kaya-group.eu' },
                { icon: Phone, text: '+383 48 781 888 (Kosovë)' },
                { icon: Phone, text: '+355 67 200 0026 (Shqipëri)' },
                { icon: MapPin, text: 'Prishtinë, Kosovë | Tiranë, Shqipëri' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-gray-400 hover:text-[#C5A05E] transition-colors group cursor-pointer">
                  <item.icon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span className="text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white flex items-center gap-2">
              <div className="w-1 h-6 bg-gradient-to-b from-[#C5A05E] to-transparent rounded-full" />
              {t.footer.quickLinks}
            </h3>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.key}>
                  <button
                    onClick={() => onNavigate(link.section)}
                    className="text-gray-400 hover:text-[#C5A05E] transition-all duration-300 hover:translate-x-2 inline-flex items-center gap-2 group"
                  >
                    <div className="w-0 h-px bg-[#C5A05E] group-hover:w-4 transition-all duration-300" />
                    {t.nav[link.key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Links Column */}
          <div>
            <h3 className="text-lg font-bold mb-6 text-white flex items-center gap-2">
              <div className="w-1 h-6 bg-gradient-to-b from-[#C5A05E] to-transparent rounded-full" />
              {t.footer.followUs}
            </h3>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`relative group w-12 h-12 backdrop-blur-xl bg-white/5 border border-white/10 hover:border-white/20 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 hover:-translate-y-1 ${social.color}`}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors relative z-10" />
                </a>
              ))}
            </div>
            
            {/* Newsletter teaser */}
            <div className="mt-8 p-4 backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl">
              <p className="text-sm text-gray-400 mb-2">Subscribe to our newsletter</p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Your email" 
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#C5A05E]"
                />
                <button className="px-4 py-2 bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] rounded-lg hover:scale-105 transition-transform">
                  <Mail className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Kaya Property Management. {t.footer.rights}
            </p>
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-2 h-2 bg-[#C5A05E] rounded-full animate-ping absolute" />
                <div className="w-2 h-2 bg-[#C5A05E] rounded-full" />
              </div>
              <span className="text-sm bg-gradient-to-r from-[#C5A05E] to-[#D4B06E] bg-clip-text text-transparent font-semibold">Powered by Excellence</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};